import { useEffect } from 'react';

/**
 * Google Tag Manager Component
 * Handles GTM initialization and event tracking
 */

// Initialize GTM
export const initializeGTM = (gtmId) => {
  if (!gtmId || process.env.REACT_APP_DEPLOYMENT_ENV === 'development') {
    console.log('GTM disabled for development environment');
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  
  // GTM script injection
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode.insertBefore(script, firstScript);

  // Initialize GTM
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });
};

// GTM Event tracking functions
export const trackEvent = (eventName, eventData = {}) => {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: eventName,
    ...eventData
  });
};

// Specific tracking functions for your shipping app
export const trackShippingQuote = (carrierData) => {
  trackEvent('shipping_quote_requested', {
    event_category: 'shipping',
    event_label: 'quote_request',
    carriers: carrierData.carriers || [],
    origin_zip: carrierData.origin || '',
    destination_zip: carrierData.destination || '',
    package_weight: carrierData.weight || '',
    package_dimensions: carrierData.dimensions || ''
  });
};

export const trackLabelPurchase = (labelData) => {
  trackEvent('label_purchased', {
    event_category: 'conversion',
    event_label: 'label_purchase',
    carrier: labelData.carrier || '',
    service_type: labelData.service || '',
    value: labelData.amount || 0,
    currency: 'USD',
    transaction_id: labelData.transactionId || ''
  });
};

export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', {
    event_category: 'engagement',
    event_label: 'user_registration',
    method: method
  });
};

export const trackPageView = (pageName, pageData = {}) => {
  trackEvent('page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_name: pageName,
    ...pageData
  });
};

// Google Ads conversion tracking
export const trackConversion = (conversionId, conversionLabel, value = null) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  const conversionData = {
    send_to: `${conversionId}/${conversionLabel}`
  };

  if (value !== null) {
    conversionData.value = value;
    conversionData.currency = 'USD';
  }

  window.gtag('event', 'conversion', conversionData);
};

// Enhanced ecommerce tracking for label purchases
export const trackPurchase = (transactionData) => {
  trackEvent('purchase', {
    transaction_id: transactionData.transactionId,
    value: transactionData.value,
    currency: 'USD',
    items: transactionData.items || []
  });

  // Also trigger Google Ads conversion
  if (process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID) {
    trackConversion(
      process.env.REACT_APP_GOOGLE_ADS_ID,
      process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID,
      transactionData.value
    );
  }
};

const GoogleTagManager = ({ children }) => {
  useEffect(() => {
    const gtmId = process.env.REACT_APP_GTM_ID;
    if (gtmId) {
      initializeGTM(gtmId);
    }
  }, []);

  return children;
};

export default GoogleTagManager;
